/** macOS vs Windows/Linux shortcut display and matching. */

export type ShortcutOs = 'mac' | 'pc'

/** Dialog-only: preview another platform's key labels. Does not change bindings or chrome hints. */
export const SHORTCUT_DIALOG_OS_STORAGE_KEY = 'flownotes_shortcuts_dialog_os'

/** @deprecated Migrated to SHORTCUT_DIALOG_OS_STORAGE_KEY; cleared on read. */
const LEGACY_SHORTCUT_OS_STORAGE_KEY = 'flownotes_shortcut_os_preference'

export function detectShortcutOs(): ShortcutOs {
  if (typeof navigator === 'undefined') return 'mac'

  const userAgentData = (navigator as Navigator & { userAgentData?: { platform?: string } })
    .userAgentData
  const platform = userAgentData?.platform ?? navigator.platform ?? ''

  if (/mac/i.test(platform)) return 'mac'
  if (/win|linux|cros|chrome os/i.test(platform)) return 'pc'
  if (/Mac/i.test(navigator.userAgent)) return 'mac'
  if (/Windows|Linux|CrOS/i.test(navigator.userAgent)) return 'pc'
  return 'mac'
}

export function readShortcutDialogOsPreference(): ShortcutOs | null {
  if (typeof localStorage === 'undefined') return null

  const stored = localStorage.getItem(SHORTCUT_DIALOG_OS_STORAGE_KEY)
  if (stored === 'mac' || stored === 'pc') return stored

  const legacy = localStorage.getItem(LEGACY_SHORTCUT_OS_STORAGE_KEY)
  if (legacy === 'mac' || legacy === 'pc') {
    localStorage.setItem(SHORTCUT_DIALOG_OS_STORAGE_KEY, legacy)
    localStorage.removeItem(LEGACY_SHORTCUT_OS_STORAGE_KEY)
    return legacy
  }

  return null
}

export function writeShortcutDialogOsPreference(os: ShortcutOs): void {
  localStorage.setItem(SHORTCUT_DIALOG_OS_STORAGE_KEY, os)
}

export function clearShortcutDialogOsPreference(): void {
  localStorage.removeItem(SHORTCUT_DIALOG_OS_STORAGE_KEY)
  localStorage.removeItem(LEGACY_SHORTCUT_OS_STORAGE_KEY)
}

/** Command on Mac, Control on Windows/Linux. */
export function isModKey(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey
}

export type AppPanel = 'sidebar' | 'center' | 'agent'

const PANEL_SELECTORS: Record<AppPanel, string> = {
  sidebar: '[data-testid="left-sidebar"]',
  center: '[data-testid="resizable-layout-main"]',
  agent: '[data-testid="chat-pane"]',
}

export function focusAppPanel(target: AppPanel): void {
  const el = document.querySelector(PANEL_SELECTORS[target]) as HTMLElement | null
  if (!el) return
  if (!el.hasAttribute('tabindex')) {
    el.tabIndex = -1
  }
  el.focus()
}

export function modKeyLabel(os: ShortcutOs): string {
  return os === 'mac' ? '\u2318' : 'Ctrl'
}

export function altKeyLabel(os: ShortcutOs): string {
  return os === 'mac' ? '\u2325' : 'Alt'
}

/** Compact label for tooltips and header hints (e.g. Cmd+Shift+F vs Ctrl+Shift+F). */
export function formatModShortcut(
  os: ShortcutOs,
  key: string,
  options?: { shift?: boolean; alt?: boolean }
): string {
  const shift = options?.shift ?? false
  const alt = options?.alt ?? false

  if (os === 'mac') {
    return `${alt ? '\u2325' : ''}${shift ? '\u21e7' : ''}\u2318${key.toUpperCase()}`
  }

  const parts = ['Ctrl']
  if (alt) parts.push('Alt')
  if (shift) parts.push('Shift')
  parts.push(key.toUpperCase())
  return parts.join('+')
}
